import { unzipSync, zipSync, strFromU8, strToU8 } from "fflate";

// write-excel-file üretilen dosyaya hücre kilidi koyamıyor (v4'te sheetProtection desteği yok).
// Ürettiği .xlsx zaten düz bir zip + küçük XML: paketi açıp iki dosyaya dokunuyoruz.
//
// xlsx'te kilit iki parçalı çalışır: her hücre varsayılan olarak "locked" işaretlidir ama bu
// yalnızca sayfa korumaya alındığında devreye girer. Yani düzenlenebilir kalması gereken
// kolonların hücrelerini açıkça locked="0" yapıp sayfayı koruyoruz.

const SHEET_PATH = "xl/worksheets/sheet1.xml";
const STYLES_PATH = "xl/styles.xml";
const WORKBOOK_PATH = "xl/workbook.xml";

// Panelden indirilen dosyayı tanımak için gizli imza sayfası. Yükleme yalnızca bu imzayı
// taşıyan, aynı kapsamda indirilmiş ve kimlik kolonları değişmemiş dosyayı kabul eder.
export const META_SHEET = "_noxus";
export const META_MARKER = "NOXUS_EXPORT";

// Kimlik kolonlarının parmak izi: yalnız fiyat değişmişse aynı kalır, kimlik kolonlarına
// dokunulduğunda (ya da satır eklenip silindiğinde) değişir.
export const identityFingerprint = async (rows) => {
  // Değerler trim'lenir: read-excel-file okurken baştaki/sondaki boşluğu atıyor. Ürün adında
  // boşluk varsa (prod'da var) indirmedeki imza yüklemede tutmuyor, dosya haksız yere
  // reddediliyordu.
  //
  // Ayırıcılar veride geçemeyecek kontrol karakterleri: düz birleştirmede farklı satırlar
  // aynı diziye düşüp aynı parmak izini verebilirdi.
  const canonical = rows
    .map((row) => row.map((value) => String(value ?? "").trim()).join("\u001F"))
    .join("\u001E");
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonical),
  );

  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
};

// Şifresiz koruma: kullanıcı isterse Excel'den "Korumayı Kaldır" ile açabilir; amaç kazara
// düzenlemeyi engellemek, veriyi kilitlemek değil.
//
// selectLockedCells / selectUnlockedCells bilerek yok: bu attribute'lar tersten çalışır, "1"
// ilgili seçimin YASAK olduğu anlamına gelir. Yazılırsa fiyat hücreleri bile seçilemez hale gelir.
const SHEET_PROTECTION =
  '<sheetProtection sheet="1" objects="1" scenarios="1"/>';

const columnIndex = (letters) => {
  let index = 0;
  for (const char of letters) {
    index = index * 26 + (char.charCodeAt(0) - 64);
  }
  return index - 1;
};

const columnLetter = (index) => {
  let letters = "";
  let remaining = index;

  while (remaining >= 0) {
    letters = String.fromCharCode(65 + (remaining % 26)) + letters;
    remaining = Math.floor(remaining / 26) - 1;
  }

  return letters;
};

const escapeXml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Formülde string literali çift tırnakla sarılır; içindeki tırnak ikilenerek kaçırılır.
const formulaLiteral = (value) =>
  typeof value === "number"
    ? String(value)
    : `"${String(value ?? "").replace(/"/g, '""')}"`;

// Google Sheets sheetProtection'ı import ederken atıyor — oradaki tek kilit mekanizması
// data validation.
//
// Formül hücrenin KENDİ değerini literal olarak taşır: değer olduğu gibi kaldıkça TRUE (hücre
// temiz görünür), kullanıcı başka bir şey yazınca FALSE (errorStyle="stop" girişi reddeder).
//
// Neden hücre başına ayrı kural: aralık + göreli referans (`A2='_kilit'!A2` gibi) Sheets'te
// çözülmüyordu — kural hem FALSE'a düşüyor hem enforcement kalkıyordu. Literal formülde
// referans yok, çözülecek bir şey de yok.
const buildLockValidations = (rows, lockedColumns) =>
  rows.flatMap((row, rowIndex) =>
    lockedColumns.map((columnIndex) => {
      const cell = `${columnLetter(columnIndex)}${rowIndex + 2}`;
      const formula = `${cell}=${formulaLiteral(row[columnIndex]?.value)}`;

      return (
        `<dataValidation type="custom" errorStyle="stop" allowBlank="0" showInputMessage="0"` +
        ` showErrorMessage="1" errorTitle="Kilitli kolon"` +
        ` error="Bu kolon ürün eşleştirmesi için kullanılıyor, düzenlenemez."` +
        ` sqref="${cell}"><formula1>${escapeXml(formula)}</formula1></dataValidation>`
      );
    }),
  );

// Fiyat kolonları: metin/harf reddedilir, negatif reddedilir. Burada göreli referans yok
// (formula1 sabit), o yüzden aralık sqref güvenli.
const buildNumericValidations = (numericColumns, rowCount) =>
  numericColumns.map((index) => {
    const letter = columnLetter(index);

    return (
      `<dataValidation type="decimal" operator="greaterThanOrEqual" errorStyle="stop"` +
      ` allowBlank="1" showInputMessage="0" showErrorMessage="1" errorTitle="Geçersiz fiyat"` +
      ` error="Bu alana yalnızca sayı yazılabilir." sqref="${letter}2:${letter}${rowCount + 1}">` +
      `<formula1>0</formula1></dataValidation>`
    );
  });

// Gizlenecek yardımcı sayfa (imza sayfası kullanıcının işine yaramaz).
const hideSheet = (workbookXml, name) =>
  workbookXml.replace(
    new RegExp(`(<sheet\\b[^>]*\\bname="${name}")`),
    '$1 state="hidden"',
  );

// Kilitsiz stili cellXfs'in sonuna ekler, indexini döner. Varsayılan (0 numaralı) xf'i baz alır —
// veri hücrelerinin stili zaten o.
const addUnlockedStyle = (stylesXml) => {
  const match = stylesXml.match(/<cellXfs count="(\d+)">/);

  if (!match) {
    return null;
  }

  const styleIndex = Number(match[1]);
  const patched = stylesXml
    .replace(match[0], `<cellXfs count="${styleIndex + 1}">`)
    .replace(
      "</cellXfs>",
      '<xf applyProtection="1"><protection locked="0"/></xf></cellXfs>',
    );

  return { styleIndex, stylesXml: patched };
};

const applyUnlockedStyle = (sheetXml, unlockedColumns, styleIndex) => {
  const unlocked = new Set(unlockedColumns);

  return sheetXml.replace(
    /<c r="([A-Z]+)(\d+)"([^>]*?)(\/?)>/g,
    (cell, letters, row, attrs, selfClosing) => {
      // 1. satır başlık — kilitli kalsın.
      if (Number(row) === 1 || !unlocked.has(columnIndex(letters))) {
        return cell;
      }

      const withoutStyle = attrs.replace(/\s*s="\d+"/, "");

      return `<c r="${letters}${row}" s="${styleIndex}"${withoutStyle}${selfClosing}>`;
    },
  );
};

// `lockedColumns` 0 tabanlı kolon indexleri; kalan kolonlar sayı-kısıtlı ve düzenlenebilir kalır.
// `rows` başlık hariç veri satırları — kilit doğrulaması hücrelerin kendi değerini formüle gömüyor.
// Kilit iki mekanizmayla kurulur: Excel sheetProtection'a, Google Sheets data validation'a bakar.
// `hiddenSheet` verilirse o sekme gizlenir (imza sayfası).
export const protectSheetColumns = (
  data,
  { columnCount, lockedColumns, rows, hiddenSheet },
) => {
  const files = unzipSync(new Uint8Array(data));

  if (!files[SHEET_PATH] || !files[STYLES_PATH]) {
    return data;
  }

  const styles = addUnlockedStyle(strFromU8(files[STYLES_PATH]));

  if (!styles) {
    return data;
  }

  const locked = new Set(lockedColumns);
  const unlockedColumns = Array.from(
    { length: columnCount },
    (_, index) => index,
  ).filter((index) => !locked.has(index));
  const sheetXml = applyUnlockedStyle(
    strFromU8(files[SHEET_PATH]),
    unlockedColumns,
    styles.styleIndex,
  );
  const validations = [
    ...buildLockValidations(rows, lockedColumns),
    ...buildNumericValidations(unlockedColumns, rows.length),
  ];
  const validationsXml = validations.length
    ? `<dataValidations count="${validations.length}">${validations.join("")}</dataValidations>`
    : "";

  // Şema sırası: sheetData → sheetProtection → dataValidations.
  files[SHEET_PATH] = strToU8(
    sheetXml.replace(
      "</sheetData>",
      `</sheetData>${SHEET_PROTECTION}${validationsXml}`,
    ),
  );
  files[STYLES_PATH] = strToU8(styles.stylesXml);

  if (hiddenSheet && files[WORKBOOK_PATH]) {
    files[WORKBOOK_PATH] = strToU8(
      hideSheet(strFromU8(files[WORKBOOK_PATH]), hiddenSheet),
    );
  }

  return zipSync(files);
};

// write-excel-file'ın toFile()'ı kendi indirmesini tetikliyordu; koruma eklemek için araya
// girince blob elimizde kaldı, indirmeyi kendimiz başlatıyoruz.
export const downloadFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
};
